<!--
© Copyright 2015-2026 Personalized Privacy Assistant Project, Carnegie Mellon University - All Rights Reserved - US Patent 11,768,949, US Patent 10,956,586, US Patent 12,235,973, and additional USPTO and EPO patents pending.
This code is proprietary and confidential. For any questions about this code, including licensing, please contact Prof. Norman Sadeh (sadeh@cs.cmu.edu).
-->
<template>
  <div class="h-100">
    <div class="card bg-light mb-3 h-100">

      <!-- Transfer IRR Modal -->
      <div class="modal fade" id="transferModal" tabindex="-1" role="dialog" aria-labelledby="transferModalLabel" aria-hidden="true" style="z-index:999999;">
        <div class="modal-dialog modal-lg" role="document" style="max-width: 750px;">
          <div class="modal-content">
            <div class="modal-header border-0" style="display: flex; justify-content: flex-end; padding: 15px 20px;">
              <button type="button" class="close" data-dismiss="modal" aria-label="Cancel" style="position: static; margin-left: auto;">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

              <div class="modal-body" style="padding: 0 30px 30px 30px;">
                <h4 style="color: #0051BA; font-weight: bold; margin-bottom: 20px;">Transfer Registry Ownership</h4>
                <p style="font-size: 16px; line-height: 1.6; margin-bottom: 25px;">By clicking "Request Transfer", an email will be sent to the new owner inviting them to accept ownership of this registry. <strong>You remain the owner until they accept</strong>, and you can cancel the request at any time before then. When they accept, your ownership of this registry ends and, if they already hold a role in this registry (e.g. admin), it is replaced by the owner role. Only one transfer request can be pending at a time. The new owner must have an existing IoTPI account.</p>

                <form id="transferForm" method="POST" enctype="multipart/form-data" :action="baseUrls.transferUrl">
                  <input type="hidden" name="_token" :value="csrf_token">
                  <input type="hidden" name="irr" :value="irr.id">

                  <div class="form-group" style="display: flex; align-items: flex-start; gap: 12px; margin-bottom: 12px;">
                    <label for="new_owner_email" style="font-weight: bold; font-size: 16px; color: #333; white-space: nowrap; padding-top: 8px; flex-shrink: 0;">New Owner Email</label>
                    <div style="flex-grow: 1;">
                      <input type="email" id="new_owner_email" name="new_owner_email" placeholder="" required="required" class="form-control" @input="validateEmail" style="margin-bottom: 8px;">
                      <small id="new_owner_email-help" class="form-text text-muted">Note: The email address of the new owner must be what they use for their IoTPI account</small>
                    </div>
                  </div>

                  <div class="d-flex gap-2 pt-2" style="gap: 10px;">
                    <button type="button" class="btn" style="background-color: #0051BA; color: white; border-radius: 25px; padding: 8px 16px; border: none; font-weight: 500; font-size: 14px; cursor: pointer;" data-dismiss="modal">Cancel</button>
                    <button type="submit" id="transfer-form-button" class="btn" style="background-color: #F4A582; color: white; border-radius: 25px; padding: 8px 16px; border: none; font-weight: 500; font-size: 14px; cursor: pointer;" :disabled="!isEmailValid" :style="{opacity: isEmailValid ? 1 : 0.6, cursor: isEmailValid ? 'pointer' : 'not-allowed'}">Request Transfer</button>
                  </div>
                </form>
              </div>

          </div>
        </div>
      </div>


      <!-- Cancel Transfer Ownership Request Modal -->
      <div v-if="hasPendingTransfer" class="modal fade" id="cancelTransferModal" tabindex="-1" role="dialog" aria-labelledby="cancelTransferModalLabel" aria-hidden="true" style="z-index:999999;">
        <div class="modal-dialog modal-lg" role="document" style="max-width: 750px;">
          <div class="modal-content">
            <div class="modal-header border-0" style="display: flex; justify-content: flex-end; padding: 15px 20px;">
              <button type="button" class="close" data-dismiss="modal" aria-label="Close" style="position: static; margin-left: auto;">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

              <div class="modal-body" style="padding: 0 30px 30px 30px;">
                <h4 id="cancelTransferModalLabel" style="color: #0051BA; font-weight: bold; margin-bottom: 20px;">Cancel Transfer Ownership Request</h4>
                <p style="font-size: 16px; line-height: 1.6; margin-bottom: 25px;">
                  A request to transfer ownership of this registry to <strong>{{ ownershipTransfer.pending.newOwnerName }}</strong> ({{ ownershipTransfer.pending.newOwnerEmail }}) was sent on {{ ownershipTransfer.pending.requestedAt }} and has not been accepted yet.
                  If it is not accepted it expires on its own on {{ ownershipTransfer.pending.expiresAt }}.
                  Canceling now withdraws the invitation immediately; the current owner keeps ownership and a new request can be sent afterwards.
                </p>

                <form method="POST" :action="ownershipTransfer.pending.cancelUrl">
                  <input type="hidden" name="_token" :value="csrf_token">
                  <div class="d-flex gap-2 pt-2" style="gap: 10px;">
                    <button type="button" class="btn" style="background-color: #0051BA; color: white; border-radius: 25px; padding: 8px 16px; border: none; font-weight: 500; font-size: 14px; cursor: pointer;" data-dismiss="modal">Keep Request</button>
                    <button type="submit" class="btn btn-danger" style="border-radius: 25px; padding: 8px 16px; font-weight: 500; font-size: 14px;">Cancel Transfer Request</button>
                  </div>
                </form>
              </div>

          </div>
        </div>
      </div>

      <div class="card-body">
        <div class="card-text">
          <!-- ownership transfer flash messages -->
          <div v-if="ownershipTransfer && ownershipTransfer.status" class="alert alert-success" role="alert">{{ ownershipTransfer.status }}</div>
          <div v-if="ownershipTransfer && ownershipTransfer.error" class="alert alert-danger" role="alert">{{ ownershipTransfer.error }}</div>

          <!-- invitation addressed to the current user -->
          <div v-if="incomingTransfer" class="alert alert-info" role="alert">
            <p class="mb-2">
              <strong>{{ incomingTransfer.requestedByName }}</strong> ({{ incomingTransfer.requestedByEmail }}) has invited you to become the owner of this registry<span v-if="!incomingTransfer.requestedByIsCurrentOwner">, currently owned by <strong>{{ incomingTransfer.currentOwnerName }}</strong></span>.
              Nothing changes until you accept. This invitation expires on <strong>{{ incomingTransfer.expiresAt }}</strong>.
            </p>
            <div class="d-flex flex-wrap align-items-center" style="gap: 10px;">
              <form method="POST" :action="incomingTransfer.acceptUrl" style="display:inline;">
                <input type="hidden" name="_token" :value="csrf_token">
                <button type="submit" class="btn btn-sm btn-registry">Accept Ownership</button>
              </form>
              <form method="POST" :action="incomingTransfer.declineUrl" style="display:inline;">
                <input type="hidden" name="_token" :value="csrf_token">
                <button type="submit" class="btn btn-sm btn-danger">Decline</button>
              </form>
              <a :href="incomingTransfer.reviewUrl" class="small">Review request details</a>
            </div>
          </div>

          <!-- pending request sent from this registry (visible to the owner / requester / system admins) -->
          <div v-if="canTransfer && hasPendingTransfer" class="alert alert-warning" role="alert">
            <strong>Ownership transfer pending.</strong>
            A request to transfer ownership of this registry to <strong>{{ ownershipTransfer.pending.newOwnerName }}</strong> ({{ ownershipTransfer.pending.newOwnerEmail }}) was sent on {{ ownershipTransfer.pending.requestedAt }}<span v-if="!ownershipTransfer.pending.requestedByIsCurrentOwner"> by {{ ownershipTransfer.pending.requestedByName }}</span>.
            The current owner remains the owner until they accept. The request expires on {{ ownershipTransfer.pending.expiresAt }} if not accepted. <a :href="ownershipTransfer.pending.reviewUrl">View request</a>
          </div>

          <center>
            <h1>Registry:&nbsp;&nbsp;{{ irr.name }}</h1>
            <p><small>Registry ID # {{ irr.id }}</small></p>
            <p class="lead text-muted">{{ irr.description }}</p>
          </center>
          <!--
          <table class="table">
            <tbody>
              <tr>
                <th><h6>RESOURCES:</h6></th>
                <th><h6>{{ resources.length }}</h6></th>
              </tr>
              <tr>
                <th><h6>CONTRIBUTORS:</h6></th>
                <th><h6>{{ numUsers }}</h6></th>
              </tr>
              <tr>
                <th v-if="!permissions.currentPermissions.IRR_CHANGE_SETTINGS"><h6>CREATED:</h6></th>
                <th v-if="!permissions.currentPermissions.IRR_CHANGE_SETTINGS"><h6>{{ irr.created_at.split(" ", 1)[0] }}</h6></th>
              </tr>
            </tbody>
          </table>
        -->
          <div v-if="permissions.currentPermissions.IRR_CHANGE_SETTINGS" class="row">
              <div class="col-6">
                <a :href="'/irr/' + irr.id + '/edit'" class="btn btn-registry btn-block" role="button" aria-disabled="true">EDIT SETTINGS</a>
                <a :href="'/irr/' + irr.id + '/publishrequests'" class="btn btn-resource btn-block" role="button" aria-disabled="true">PUBLICATION REQUESTS<br />({{ numPendingPublishRequests }} Pending)</a>
              </div>
              <div class="col-6">
                <a :href="'/irr/' + irr.id + '/users'" class="btn btn-registry btn-block" role="button" aria-disabled="true">REGISTRY USERS<br />({{ numUsers }} {{ numUsers === 1 ? 'Member' : 'Members' }})</a>
                <a v-if="canTransfer && !hasPendingTransfer" class="btn btn-registry btn-block" role="button" data-toggle="modal" data-target="#transferModal" aria-disabled="true">TRANSFER OWNERSHIP</a>
                <a v-if="canTransfer && hasPendingTransfer" class="btn btn-warning btn-block" role="button" data-toggle="modal" data-target="#cancelTransferModal" aria-disabled="true">CANCEL TRANSFER OWNERSHIP REQUEST</a>
                <!-- <a :href="'/irr/' + irr.id + '/accessrequests'" class="btn btn-secondary btn-block" role="button" aria-disabled="true">ACCESS REQUESTS</a> -->
              </div>
          </div>
          <!-- <div v-else-if="!permissions.currentPermissions.IRR_SUBMIT_RESOURCE" class="row">
            <div class="col-12">
              <a :href="'/irr/' + irr.id + '/requestaccess'" class="btn btn-registry btn-block" role="button" aria-disabled="true">REQUEST CONTRIBUTOR ACCESS</a>
            </div>
          </div> -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
    export default {
      props: {
        irr: Object,
        permissions: Object,
        resources: Array,
        numUsers: Number,
        numPendingPublishRequests: Number,
        baseUrls: Object,
        csrf_token: String,
        // { pending: {...}|null, incoming: {...}|null, error: '', status: '' } - see App\Support\OwnershipTransfers::viewPayload()
        ownershipTransfer: {
          type: Object,
          default: () => ({ pending: null, incoming: null, error: '', status: '' })
        },
      },
      data() {
        return {
          isEmailValid: false,
        }
      },
      mounted(){
        const urlParams = new URLSearchParams(window.location.search);

        //check for 'transfer' in query string, display the transfer modal (or the cancel modal if a request is already pending) if applicable
        if(this.canTransfer) {
          if(urlParams.get('transfer') == 1) {
            if(this.hasPendingTransfer) {
              $('#cancelTransferModal').modal('show');
            } else {
              $('#transferModal').modal('show');
            }
          }
        } else {
          $('#transferModal').html('');
        }
      },
      methods: {
        validateEmail() {
          const email = document.getElementById('new_owner_email').value;
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          this.isEmailValid = emailRegex.test(email);
        }
      },
      computed: {
        canTransfer: function () {
          return !!(this.irr.isOwner || (this.permissions && this.permissions.currentPermissions && this.permissions.currentPermissions.SYS_TRANSFER_IRR));
        },
        // a transfer request has been sent and is awaiting the invited user's response
        hasPendingTransfer: function () {
          return !!(this.ownershipTransfer && this.ownershipTransfer.pending);
        },
        // the current user is the one invited to take ownership
        incomingTransfer: function () {
          return (this.ownershipTransfer && this.ownershipTransfer.incoming) ? this.ownershipTransfer.incoming : null;
        },
        daysLive: function() {
          var currentDate = new Date();
          var createdDate = new Date(this.irr.created_at);
          var difference = (currentDate - createdDate) / 1000 / 86400;
          return (difference < 1) ? 'less than a day' : Math.floor(difference);
        }
      }
    }
</script>
